/**
 * DemoMode — Interactive "Demo Mode" director (Provider).
 *
 * Owner-locked 2026-06-10. NOT a simulator: this auto-logs the visitor into the
 * REAL Provider Portal as the isolated demo tenant and Ari DRIVES the real app —
 * each step hits the real /api endpoints (creating real demo-tenant records),
 * then the director navigates to the real portal screen so the visitor watches
 * the REAL UI update. A deep-blue Ari "director dock" narrates; a persistent
 * "DEMO MODE — sample data · no charges" banner stays visible; Exit returns to
 * the Gateway and clears the demo session.
 *
 * Mounted once in the App shell. Activates on the `demo:start` event (fired by
 * launchProviderDemo) or when localStorage `demo_mode === 'provider'`.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Play, Pause, SkipForward, RotateCcw, X, ArrowRight, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// step.page (backend) → real portal route. The director navigates to the
// SPECIFIC real record that PROVES each step (per the Demo Reality doctrine):
//   • customers → the customer's record   • invoice → that exact invoice
//   • jobs/schedule/job/tracking → the live dispatch board (job, tech, map pin)
//   • remodpay → the populated dispatch board; the genuine 9-envelope split is
//     rendered in the Ari dock (the business-wallet ledger accumulates across
//     demo runs, so it can't cleanly prove a single $726.80 collect today).
const resolveDemoRoute = (page, st = {}) => {
  switch (page) {
    case 'customers': return st.customer_id ? `/customer/${st.customer_id}` : '/jobs';
    case 'invoice':   return st.invoice_id ? `/invoice/${st.invoice_id}` : '/invoices';
    case 'remodpay':
    case 'dashboard': return '/dashboard';
    default:          return '/jobs'; // jobs, schedule, job, tracking
  }
};

const DEMO_LS = ['demo_mode', 'demo_session', 'token', 'company_id', 'user_id',
  'user_name', 'user_role', 'analytics_user', 'enabled_features',
  'hasCompletedOnboarding', 'first_login_complete'];

/** Boot a Provider Demo: reset the demo tenant, log in as it, enter the portal. */
export async function launchProviderDemo(navigate) {
  const r = await fetch(`${API}/api/demo/session/provider`, { method: 'POST' });
  if (!r.ok) throw new Error('demo session failed');
  const s = await r.json();
  localStorage.setItem('token', s.token);
  localStorage.setItem('company_id', s.company_id);
  localStorage.setItem('user_id', s.account_id);
  localStorage.setItem('user_name', s.provider_name);
  localStorage.setItem('user_role', 'admin');
  localStorage.setItem('analytics_user', JSON.stringify({
    id: s.account_id, company_id: s.company_id, name: s.provider_name,
    role: 'admin', email: 'demo-provider@remoddash.com',
  }));
  localStorage.setItem('enabled_features', JSON.stringify(s.enabled_features || []));
  localStorage.setItem('hasCompletedOnboarding', 'true');
  localStorage.setItem('first_login_complete', 'true');
  localStorage.setItem('demo_session', JSON.stringify(s));
  localStorage.setItem('demo_mode', 'provider');
  window.dispatchEvent(new Event('demo:start'));
  navigate('/dashboard');
}

const DemoMode = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(() => localStorage.getItem('demo_mode') === 'provider');
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem('demo_session') || 'null'); } catch { return null; }
  });
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState({});
  const [playing, setPlaying] = useState(true);
  const [finished, setFinished] = useState(false);
  const [chars, setChars] = useState(0);
  const [narr, setNarr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const advanceRef = useRef(null);
  const typeRef = useRef(null);
  const ranRef = useRef(-1);
  const stateRef = useRef({});

  const steps = session?.steps || [];
  const step = steps[idx];

  const clearTimers = useCallback(() => {
    if (advanceRef.current) clearTimeout(advanceRef.current);
    if (typeRef.current) clearInterval(typeRef.current);
  }, []);

  // Activate on launch
  useEffect(() => {
    const onStart = () => {
      try { setSession(JSON.parse(localStorage.getItem('demo_session') || 'null')); } catch { /* */ }
      setIdx(0); setState({}); stateRef.current = {}; setFinished(false);
      setPlaying(true); setError(null); ranRef.current = -1; setActive(true);
    };
    window.addEventListener('demo:start', onStart);
    return () => window.removeEventListener('demo:start', onStart);
  }, []);

  // Mark the body while a demo is active so the global view-mode toggle and
  // other public chrome can be suppressed for a clean, distraction-free demo.
  useEffect(() => {
    if (active && session) document.body.setAttribute('data-demo', 'provider');
    else document.body.removeAttribute('data-demo');
    return () => document.body.removeAttribute('data-demo');
  }, [active, session]);

  const exit = useCallback(async () => {
    clearTimers();
    try { await fetch(`${API}/api/demo/session/provider/end`, { method: 'POST' }); } catch { /* */ }
    DEMO_LS.forEach((k) => localStorage.removeItem(k));
    setActive(false);
    navigate('/');
  }, [clearTimers, navigate]);

  const typeNarration = useCallback((text, dur) => {
    setNarr(text); setChars(0);
    if (typeRef.current) clearInterval(typeRef.current);
    const total = text.length || 1;
    const ms = Math.max(16, (dur - 1800) / total);
    let c = 0;
    typeRef.current = setInterval(() => { c += 1; setChars(c); if (c >= total) clearInterval(typeRef.current); }, ms);
  }, []);

  // Director: execute the current step against the REAL app, then navigate to
  // the real screen so the visitor watches the real UI update.
  // StrictMode-safe: the `ranRef` guard guarantees exactly one execution per
  // step index, and we never abort the in-flight call (skip is disabled while
  // busy and auto-advance only schedules when not busy, so idx can't change
  // mid-step). This prevents the dev double-invoke from deadlocking `busy`.
  useEffect(() => {
    if (!active || finished || !step) return undefined;
    if (ranRef.current === idx) return undefined;  // already executed this idx
    ranRef.current = idx;
    setBusy(true); setError(null); setSummary(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/demo/run/provider/${idx}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: session.token, state: stateRef.current }),
        });
        const data = await r.json();
        if (!data.success) { setError(data.error || 'This step hit a real error.'); setBusy(false); return; }
        stateRef.current = data.state || stateRef.current;
        setState(stateRef.current);
        setSummary(data.summary || null);
        // Navigate to the real screen that proves this step (bounce through
        // /__demo to force remount → refetch). Uses the ids the step just wrote.
        const target = resolveDemoRoute(step.page, stateRef.current);
        navigate('/__demo', { replace: true });
        setTimeout(() => navigate(target), 90);
        setBusy(false);
      } catch (e) {
        setError(String(e?.message || e)); setBusy(false);
      }
    })();
    return undefined;
  }, [active, finished, idx, step, session, navigate]);

  // Narration + auto-advance (paused-aware)
  useEffect(() => {
    if (!active || finished || !step || busy || error) return undefined;
    clearTimers();
    const dur = Math.min(11000, Math.max(6500, (step.narration || '').length * 62));
    if (!playing) { setNarr(step.narration); setChars(step.narration.length); return undefined; }
    typeNarration(step.narration, dur);
    advanceRef.current = setTimeout(() => {
      if (idx < steps.length - 1) setIdx((i) => i + 1);
      else { setFinished(true); setPlaying(false); }
    }, dur);
    return clearTimers;
  }, [active, finished, step, busy, error, playing, idx, steps.length, clearTimers, typeNarration]);

  const skip = () => {
    if (busy) return;  // never advance mid-step — it would cancel the in-flight real call and break state chaining
    clearTimers();
    if (idx < steps.length - 1) setIdx((i) => i + 1);
    else { setFinished(true); setPlaying(false); }
  };
  const restart = async () => {
    clearTimers(); setBusy(true);
    try { await launchProviderDemo(navigate); } catch { setError('Could not restart demo.'); }
    setBusy(false);
  };

  if (!active || !session) return null;

  return (
    <>
      {/* Persistent DEMO banner */}
      <div className="fixed top-0 left-0 right-0 z-[10000] overflow-hidden flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 text-white text-[10px] sm:text-sm font-semibold"
        style={{ background: 'linear-gradient(90deg,#172554,#1e3a8a,#172554)', paddingTop: 'calc(0.375rem + env(safe-area-inset-top, 0px))' }}
        data-testid="demo-mode-banner">
        <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-300 shrink-0" />
        <span className="whitespace-nowrap">DEMO MODE — sample data · no charges</span>
        <button onClick={exit} data-testid="demo-mode-exit-top"
          className="ml-1 sm:ml-3 shrink-0 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-[10px] sm:text-[11px]">
          <X className="w-3 h-3" /> Exit
        </button>
      </div>

      {/* Ari director dock (deep presidential blue) */}
      <div className="fixed bottom-0 left-0 right-0 z-[10000] px-3 sm:px-5 pt-2"
        style={{ background: 'linear-gradient(180deg, rgba(10,18,48,0) 0%, rgba(10,18,48,0.9) 35%)', paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
        data-testid="demo-director">
        <div className="max-w-5xl mx-auto rounded-2xl px-4 py-3"
          style={{ background: 'rgba(12,26,64,0.92)', border: '1px solid rgba(96,140,230,0.4)', backdropFilter: 'blur(10px)', boxShadow: '0 -8px 40px -10px rgba(0,0,0,0.5)' }}>
          {/* progress */}
          <div className="flex gap-1 mb-2.5" data-testid="demo-stepper">
            {steps.map((s, i) => (
              <div key={s.id} className="h-1 flex-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.12)' }}>
                <div className="h-full rounded-full transition-all duration-300"
                  style={{ width: i <= idx || finished ? '100%' : '0%', background: '#60A5FA', opacity: (i < idx || finished) ? 0.6 : i === idx ? 1 : 0 }} />
              </div>))}
          </div>

          {/* Real step result — surfaces the genuine data the demo just wrote
              (invoice totals, and the real 9-envelope RemodPay split). */}
          {!finished && !error && summary && (summary.split || summary.total) && (
            <div className="mb-2.5" data-testid="demo-summary">
              {summary.split && Array.isArray(summary.split.envelopes) ? (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-300 mb-1.5">
                    RemodPay · <span data-testid="demo-summary-gross">${Number(summary.split.gross || 0).toFixed(2)}</span> in → <span data-testid="demo-summary-distributed">${Number(summary.split.total || 0).toFixed(2)}</span> distributed
                  </div>
                  <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="demo-envelopes">
                    {summary.split.envelopes.filter((e) => e.amount > 0).map((e) => (
                      <div key={e.key} className="shrink-0 rounded-lg px-2.5 py-1.5"
                        style={{ background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(96,140,230,0.3)' }}>
                        <div className="text-[10px] text-blue-200/90 whitespace-nowrap">{e.label}</div>
                        <div className="text-[13px] font-bold text-white">${Number(e.amount).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex gap-1.5 flex-wrap">
                  {summary.subtotal != null && (
                    <span className="rounded-lg px-2.5 py-1 text-[12px] text-blue-100" style={{ background: 'rgba(59,130,246,0.14)' }}>Subtotal ${Number(summary.subtotal).toFixed(2)}</span>
                  )}
                  {summary.tax != null && (
                    <span className="rounded-lg px-2.5 py-1 text-[12px] text-blue-100" style={{ background: 'rgba(59,130,246,0.14)' }}>Tax ${Number(summary.tax).toFixed(2)}</span>
                  )}
                  {summary.total != null && (
                    <span className="rounded-lg px-2.5 py-1 text-[12px] font-bold text-white" style={{ background: 'rgba(59,130,246,0.22)' }}>Total ${Number(summary.total).toFixed(2)}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {finished ? (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="demo-end">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#3B82F633' }}>
                  <Sparkles className="w-5 h-5 text-blue-300" />
                </div>
                <div className="text-white text-sm sm:text-[15px]">
                  <span className="font-bold">That was RemodDash running itself.</span> A lead became a paid, fully-distributed job — automatically.
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => { DEMO_LS.forEach((k) => localStorage.removeItem(k)); setActive(false); navigate('/landing'); }}
                  data-testid="demo-cta"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-white text-sm transition-transform hover:scale-105"
                  style={{ background: '#3B82F6' }}>
                  Sign up free <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={restart} data-testid="demo-replay" className="px-3 py-2 rounded-full text-sm text-white/85 hover:bg-white/10 transition-colors inline-flex items-center gap-1.5">
                  <RotateCcw className="w-4 h-4" /> Again
                </button>
                <button onClick={exit} data-testid="demo-exit" className="px-3 py-2 rounded-full text-sm text-white/70 hover:bg-white/10 transition-colors">Exit</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 demo-ari-float" style={{ background: '#3B82F633', boxShadow: '0 0 14px #3B82F555' }}>
                <Sparkles className="w-5 h-5 text-blue-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-300">
                  Ari · Step {idx + 1} of {steps.length}{step ? ` · ${step.title}` : ''}{busy ? ' · working…' : ''}
                </div>
                {error ? (
                  <div className="text-amber-300 text-sm flex items-center gap-1.5" data-testid="demo-step-error">
                    <AlertTriangle className="w-4 h-4" /> Real step error: {error}
                  </div>
                ) : (
                  <div className="text-white text-sm sm:text-[15px] leading-snug" data-testid="demo-narration">
                    {narr.slice(0, chars)}{chars < narr.length && <span className="inline-block w-1.5 h-4 ml-0.5 align-middle demo-caret bg-blue-300" />}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => setPlaying((p) => !p)} data-testid="demo-play-pause"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105" style={{ background: '#3B82F6' }}>
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}<span className="hidden sm:inline">{playing ? 'Pause' : 'Play'}</span>
                </button>
                <button onClick={skip} disabled={busy} data-testid="demo-skip" className="p-2 rounded-full text-white/80 hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Skip"><SkipForward className="w-4 h-4" /></button>
                <button onClick={restart} data-testid="demo-restart" className="p-2 rounded-full text-white/80 hover:bg-white/10 transition-colors" aria-label="Restart"><RotateCcw className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes demoAriFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-3px) } }
        .demo-ari-float { animation: demoAriFloat 4s ease-in-out infinite }
        @keyframes demoCaret { 0%,100% { opacity: 1 } 50% { opacity: 0 } }
        .demo-caret { animation: demoCaret .9s step-end infinite }
        /* Keep the demo distraction-free: hide the global view-mode toggle + feedback widget */
        body[data-demo="provider"] [data-testid="global-portal-viewmode-toggle"],
        body[data-demo="provider"] [data-testid="view-mode-guidance-popover"],
        body[data-demo="provider"] [data-testid="business-setup-banner"],
        body[data-demo="provider"] [data-testid="mobile-footer-nav"],
        body[data-demo="provider"] [data-testid="ari-activation-nudge"],
        body[data-demo="provider"] [data-testid="ari-upgrade-card"],
        body[data-demo="provider"] [data-testid="feedback-widget-fab"] { display: none !important; }
        /* Mobile-only: offset page content below the fixed demo banner so the
           sticky mobile top bar isn't clipped. Desktop behavior is unchanged. */
        @media (max-width: 640px) {
          body[data-demo="provider"] { padding-top: calc(34px + env(safe-area-inset-top, 0px)) !important; }
        }
      `}</style>
    </>
  );
};

export default DemoMode;
